import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Chip,
  Alert,
  Button,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link
} from '@mui/material';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import useMercariDashboard from './useMercariDashboard';
import PriceNegotiationModal from './modal/PriceNegotiationModal';
import PurchaseRequestModal from './modal/PurchaseRequestModal';
import CustomLinkTaskModal from './modal/CustomLinkTaskModal';
import AppraisalModal from './modal/AppraisalModal';
import { toast } from 'react-toastify';
import ForceTakeModal from './modal/ForceTakeModal';
import CreateCustomLinkModal from './modal/CreateCustomLinkModal';
import DispatchAlertsSection from './DispatchAlertsSection';
import DispatchJobsSection from './DispatchJobsSection';
import DispatchNowSection from './DispatchNowSection';
import DispatchService from '../../setting/dispatch/DispatchService';
import io from 'socket.io-client';


const customStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 1000
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    height: 'auto',
    width: '600px',
    marginRight: '0%',
    transform: 'translate(-50%, -50%)',
    padding: 0,
    border: 'none',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
  },
};

const MercariAdmin = () => {
  const { 
    getDashboardItems,
    takeNewPriceNegotiationCase,
    releasePriceNegotiationCase,
    completePriceNegotiationCase,
    priceNegoDetail,
    isLoading,
    error,
    takeNewPurchaseCase,
    releasePurchaseCase,
    completePurchaseCase,
    takeAppraisalCase,
    releaseAppraisalCase,
    recordAppraisalResult,
    previewAppraisalFailure,
    deferAppraisalCheck,
    getReconcileCollections
  } = useMercariDashboard();

  // Shop the admin is currently viewing (same source as the keyData banners) —
  // the pending list is scoped to it.
  const shopName = useSelector((state) => state.shop.name);

  const [activeModal, setActiveModal] = useState(null);
  const [currentAdminInfo, setCurrentAdminInfo] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [pendingCaseId, setPendingCaseId] = useState(null);
  const [dashboardItems, setDashboardItems] = useState([]);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  // The full-page spinner is for the FIRST load only. It used to show on every
  // 2-minute refresh, and because it returned above the modals it unmounted an
  // open case — Order By + slip gone while still holding the claim (E1).
  const [initialLoading, setInitialLoading] = useState(true);
  // A failed list fetch used to look exactly like an empty queue (E4).
  const [loadError, setLoadError] = useState(null);
  // The hook's `error` is set by a failed take/release and never cleared; it
  // used to replace the whole board until a reload (E3).
  const [dismissedError, setDismissedError] = useState(null);
  const [createLinkOpen, setCreateLinkOpen] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null); // { paymentUrl?, netSettlement?, refunds? }
  // Pending reconcile collections (guest +Δ links) — re-findable after the result dialog closes.
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const [collections, setCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionsShowAll, setCollectionsShowAll] = useState(false);

  // Add refs to track current state values
  const selectedCaseRef = useRef(null);

  // Update refs whenever state changes
  useEffect(() => {
    selectedCaseRef.current = selectedCase;
  }, [selectedCase]);

  // Initial load and periodic refresh
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setIsLoadingDashboard(true);
        const response = await getDashboardItems(shopName);
        console.log('Fetched dashboard items:', response); // Debug log
        setDashboardItems(response.items || []); // Ensure we always set an array
        setLoadError(null);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        setLoadError('โหลดรายการไม่สำเร็จ — รายการที่แสดงอาจไม่เป็นปัจจุบัน กรุณากด F5 เพื่อรีเฟรช');
      } finally {
        setIsLoadingDashboard(false);
        setInitialLoading(false);
      }
    };

    // Initial load
    loadDashboardData();

    // Set up periodic refresh every 2 minutes
    const refreshInterval = setInterval(loadDashboardData, 2 * 60 * 1000);

    // Cleanup
    return () => {
      clearInterval(refreshInterval);
    };
  }, [shopName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Socket setup and event handling
  useEffect(() => {
    // withCredentials: the namespace admits admins only, proven by the
    // admin_token cookie on the handshake (B5).
    const socket = io(`${process.env.REACT_APP_BACKEND_URL}/mercari-admin`, { withCredentials: true });

    socket.on('connect', () => {
      console.log('Connected to Mercari Admin Socket.IO');
    });

    socket.on('disconnect', () => {
      console.log('Disconnected from Mercari Admin Socket.IO');
    });

    // New request events. A row can arrive twice — the socket push and the
    // next 2-minute refresh, or a reconnect replay — so add only what is not
    // already listed (E6). Rows are still not shop-scoped: the event carries
    // no shop, and the periodic refresh (scoped) corrects the list.
    const rowKey = (row) => `${row.type}-${row.source || 'mercari'}-${row.id}`;
    const addRow = (prevItems, row) =>
      prevItems.some((item) => rowKey(item) === rowKey(row)) ? prevItems : [...prevItems, row];

    socket.on('new_negotiation_request', (data) => {
      console.log('New negotiation request:', data);
      setDashboardItems(prevItems => addRow(prevItems, {
        id: data.id,
        type: 'price_negotiation',
        source: data.source,
        nickname: data.nickname,
        requestedAt: data.requestedAt
      }));
    });

    socket.on('new_purchase_request', (data) => {
      console.log('New purchase request:', data);
      setDashboardItems(prevItems => addRow(prevItems, {
        id: data.id,
        type: 'purchase',
        source: data.source,
        nickname: data.nickname,
        requestedAt: data.requestedAt
      }));
    });

    // Price negotiation events. A Mercari negotiation and a PayPay one live in
    // different tables and can collide on a numeric id, so match on source too
    // (undefined ⇒ 'mercari' for both the row and the event payload).
    const sameNegotiation = (item, data) =>
      item.id === data.id &&
      item.type === 'price_negotiation' &&
      (item.source || 'mercari') === (data.source || 'mercari');

    socket.on('negotiation_case_taken', (data) => {
      console.log('Price negotiation case taken:', data);
      handleCaseTakenByOther(data);
      setDashboardItems(prevItems =>
        prevItems.map(item =>
          sameNegotiation(item, data)
            ? { ...item, nickname: data.nickname }
            : item
        )
      );
    });

    socket.on('negotiation_case_completed', (data) => {
      console.log('Price negotiation case completed:', data);
      setDashboardItems(prevItems =>
        prevItems.filter(item => !sameNegotiation(item, data))
      );
    });

    socket.on('negotiation_case_released', (data) => {
      console.log('Price negotiation case released:', data);
      setDashboardItems(prevItems =>
        prevItems.map(item =>
          sameNegotiation(item, data)
            ? { ...item, nickname: null }
            : item
        )
      );
    });

    // Purchase events. A mercari purchase and a custom-link order share
    // type:'purchase' and can collide on a numeric id, so match on source too
    // (undefined ⇒ 'mercari' for both the row and the event payload).
    const samePurchase = (item, data) =>
      item.id === data.id &&
      item.type === 'purchase' &&
      (item.source || 'mercari') === (data.source || 'mercari');

    socket.on('purchase_case_taken', (data) => {
      console.log('Purchase case taken:', data);
      handleCaseTakenByOther(data);
      setDashboardItems(prevItems =>
        prevItems.map(item =>
          samePurchase(item, data) ? { ...item, nickname: data.nickname } : item
        )
      );
    });

    socket.on('purchase_case_completed', (data) => {
      console.log('Purchase case completed:', data);
      setDashboardItems(prevItems =>
        prevItems.filter(item => !samePurchase(item, data))
      );
    });

    socket.on('purchase_case_released', (data) => {
      console.log('Purchase case released:', data);
      setDashboardItems(prevItems =>
        prevItems.map(item =>
          samePurchase(item, data) ? { ...item, nickname: null } : item
        )
      );
    });

    // ส่งตรวจ events. Its own type, so id + type is enough — but match source as
    // well, for the same reason purchases must: these rows carry a
    // MercariPurchase.id, which other sources also use.
    const sameAppraisal = (item, data) =>
      item.id === data.id &&
      item.type === 'appraisal' &&
      (item.source || 'mercari') === (data.source || 'mercari');

    socket.on('appraisal_case_taken', (data) => {
      handleCaseTakenByOther(data);
      setDashboardItems(prevItems =>
        prevItems.map(item =>
          sameAppraisal(item, data) ? { ...item, nickname: data.nickname } : item
        )
      );
    });

    socket.on('appraisal_case_released', (data) => {
      setDashboardItems(prevItems =>
        prevItems.map(item =>
          sameAppraisal(item, data) ? { ...item, nickname: null } : item
        )
      );
    });

    // Two ways an appraisal leaves the board, kept as separate events because
    // they mean different things even though both remove the row: 'completed' is
    // finished for good, 'deferred' is back tomorrow.
    const dropAppraisalRow = (data) =>
      setDashboardItems(prevItems => prevItems.filter(item => !sameAppraisal(item, data)));

    socket.on('appraisal_case_completed', dropAppraisalRow);
    socket.on('appraisal_case_deferred', dropAppraisalRow);

    return () => {
      socket.disconnect();
    };
  }, []); // Empty dependencies since we're using refs

  const handleCaseTaking = async (item, takeCaseFunction, modalType) => {
    try {
      // `item.source` is passed through: it routes a purchase to the custom-link
      // handler ('customlink') and a negotiation to the shared table ('paypay')
      // on the backend. An appraisal ignores it.
      const result = await takeCaseFunction(item.id, false, item.source);
      if (result.success) {
        setSelectedCase({
          id: item.id,
          type: item.type,
          source: item.source,
          ...result.data
        });
        setActiveModal(modalType);
      } else if (result.caseStatus === 'already_taken') {
        console.log('Case already taken:', item);
        setCurrentAdminInfo(result.currentAdmin);
        setPendingCaseId({
          id: item.id,
          type: item.type,
          source: item.source
        });
        setActiveModal('force');
      }
    } catch (error) {
      console.error('Error taking case:', error);
      toast.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
    }
  };

  const handleAction = async (item) => {
    switch (item.type) {
      case 'price_negotiation':
        await handleCaseTaking(item, takeNewPriceNegotiationCase, 'price_negotiation');
        break;
      
      case 'purchase':
        // Same task type, two sources: a custom-link order opens the custom-link
        // modal (proof-only confirm + per-item refund), a mercari item the usual one.
        await handleCaseTaking(
          item,
          takeNewPurchaseCase,
          item.source === 'customlink' ? 'customlink' : 'purchase'
        );
        break;

      // ส่งตรวจ: the parcel is at the inspection centre and a result is due.
      // Keyed by MercariPurchase.id like a purchase case, but its own caseType —
      // one admin holding it as an appraisal must not lock the other flow.
      case 'appraisal':
        await handleCaseTaking(item, takeAppraisalCase, 'appraisal');
        break;

      default:
        console.log('Unknown type:', item.type);
    }
  };

  const handleCloseModal = async () => {
    if (activeModal !== 'force') {
      try {
        const releaseFunction = {
          'price_negotiation': (id) => releasePriceNegotiationCase(id, selectedCase?.source),
          'purchase': releasePurchaseCase,
          'customlink': (id) => releasePurchaseCase(id, 'customlink'),
          'appraisal': releaseAppraisalCase
        }[activeModal];

        if (releaseFunction && selectedCase?.id) {
          await releaseFunction(selectedCase.id);
        }
      } catch (error) {
        console.error('Error releasing case:', error);
        toast.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      }
    }
    setActiveModal(null);
    setSelectedCase(null);
    setCurrentAdminInfo(null);
    setPendingCaseId(null);
  };

  const getTypeChip = (item) => {
    const typeConfig = {
      purchase: {
        label: "สั่งซื้อ สินค้า",
        color: "primary"
      },
      price_negotiation: {
        label: "ต่อราคา สินค้า",
        color: "secondary"
      },
      appraisal: {
        label: "ส่งตรวจ (รอผล)",
        color: "warning"
      }
    };

    // A purchase task can be a Mercari purchase or a custom-link order — same
    // type, different chip so the admin knows which modal/flow they'll get.
    // A negotiation names its marketplace when it is not Mercari.
    const config = item.type === 'purchase' && item.source === 'customlink'
      ? { label: `ลิงก์สั่งซื้อ (Custom)${item.itemCount ? ` · ${item.itemCount} ชิ้น` : ''}`, color: "warning" }
      : item.type === 'price_negotiation' && item.source === 'paypay'
        ? { label: "ต่อราคา PayPay", color: "secondary" }
        : (typeConfig[item.type] || { label: item.type, color: "default" });

    return (
      <Chip
        label={config.label}
        color={config.color}
        variant="filled"
        size="medium"
        sx={{
          '& .MuiChip-label': {
            fontSize: '1rem',
            fontWeight: 500,
            padding: '0 12px'
          }
        }}
      />
    );
  };

  const getActionButtonText = (type) => {
    const textMap = {
      purchase: "ดำเนินการ",
      price_negotiation: "ดำเนินการ",
      // The task is to go and look at Mercari, not to process anything.
      appraisal: "เช็คผลตรวจ"
    };
    return textMap[type] || "ดำเนินการ";
  };

  const openCollections = async (showAll = collectionsShowAll) => {
    setCollectionsOpen(true);
    setCollectionsLoading(true);
    const rows = await getReconcileCollections(shopName, showAll ? 'all' : 'pending');
    setCollections(rows);
    setCollectionsLoading(false);
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('คัดลอกลิงก์แล้ว');
  };

  const collectionStatusChip = (status) => {
    const map = {
      pending: { label: 'รอชำระ', color: 'warning' },
      completed: { label: 'ชำระแล้ว', color: 'success' },
      cancelled: { label: 'ยกเลิก', color: 'default' },
      failed: { label: 'ไม่สำเร็จ', color: 'error' },
    };
    const s = map[status] || { label: status, color: 'default' };
    return <Chip size="small" label={s.label} color={s.color} />;
  };

  const handleForceTake = async () => {
    if (pendingCaseId) {
      const takeCaseFunction = {
        'price_negotiation': takeNewPriceNegotiationCase,
        'purchase': takeNewPurchaseCase,
        'appraisal': takeAppraisalCase
      }[pendingCaseId.type];

      if (takeCaseFunction) {
        const result = await takeCaseFunction(pendingCaseId.id, true, pendingCaseId.source);
        if (result.success) {
          setSelectedCase({
            id: pendingCaseId.id,
            type: pendingCaseId.type,
            source: pendingCaseId.source,
            ...result.data
          });
          setActiveModal(pendingCaseId.source === 'customlink' ? 'customlink' : pendingCaseId.type);
          setCurrentAdminInfo(null);
          setPendingCaseId(null);
        }
      }
    }
  };

  const handleCaseTakenByOther = (data) => {
    // Use refs instead of state directly
    const currentSelectedCase = selectedCaseRef.current;

    if (currentSelectedCase &&
        currentSelectedCase.id === data.id &&
        currentSelectedCase.type === data.type &&
        (currentSelectedCase.source || 'mercari') === (data.source || 'mercari')) {
      // Close the modal
      setActiveModal(null);
      setSelectedCase(null);
      setCurrentAdminInfo(null);
      setPendingCaseId(null);
      
      // Show notification
      toast.warning('เคสนี้ถูกดำเนินการโดย แอดมินคนอื่นแล้ว');
    }
  };

  const handlePriceNegotiationComplete = async (data) => {
    try {
      // The open case's source routes the complete to the right table.
      const success = await completePriceNegotiationCase({
        ...data,
        ...(selectedCase?.source ? { source: selectedCase.source } : {})
      });
      if (success) {
        toast.success('ดำเนินการเสร็จสิ้น');
        setActiveModal(null); // Close modal
        setSelectedCase(null);
      } else {
        toast.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      }
    } catch (error) {
      console.error('Error completing case:', error);
      // 400 = the case is gone or another admin holds it; retrying won't help.
      if (error?.response?.status === 400) {
        toast.error('ไม่พบเคส หรือเคสนี้ไม่ได้อยู่ในความดูแลของคุณ กรุณารีเฟรชหน้า');
      } else {
        toast.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      }
    }
  };

  // What ไม่ผ่าน would do, asked before it is done so the confirmation can name
  // the real figure. Returns the payload; the modal decides how to word it.
  const handleAppraisalFailurePreview = async (orderId) => {
    const res = await previewAppraisalFailure(orderId);
    return res;
  };

  // ส่งตรวจ: the admin read the Mercari page and recorded what it said. Both
  // outcomes are terminal. A failure also ENDS the order — into the cancel funnel
  // with its reason set, and for a wallet customer the money goes back at once —
  // so the toast says which of those happened rather than a generic "saved".
  const handleAppraisalResult = async ({ orderId, result, note }) => {
    const res = await recordAppraisalResult({ orderId, result, note });
    if (!res?.success) {
      toast.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      return;
    }

    toast.success(result === 'passed' ? 'บันทึกผล: ผ่าน' : 'บันทึกผล: ไม่ผ่าน');
    if (result === 'failed') {
      // Which of the two endings happened decides what the admin must do next,
      // so it is spelled out: assume it refunded when it did not and the customer
      // is left out of pocket; assume it did not when it did and they are paid twice.
      const ending = res.ending;
      if (ending?.refundedToWallet) {
        toast.success(
          `ยกเลิกออเดอร์และคืนเงิน ฿${Number(ending.amount).toLocaleString('en-US', {
            minimumFractionDigits: 2, maximumFractionDigits: 2
          })} เข้ากระเป๋าเงินลูกค้าแล้ว`,
          { autoClose: 8000 }
        );
      } else {
        // No "remember to refund" here: the cancel list the order just joined
        // carries that, with its own reminders.
        toast.warning('ส่งออเดอร์เข้ารายการยกเลิกแล้ว (เหตุผล: ตรวจไม่ผ่าน)', {
          autoClose: 8000
        });
      }
    }
    closeCaseModal();
  };

  // "ยังไม่เสร็จ" — no result on Mercari yet. The case steps off the board for a
  // day AND gives up its claim, so tomorrow it is anyone's again.
  const handleAppraisalDefer = async (orderId) => {
    const res = await deferAppraisalCheck(orderId);
    if (!res?.success) {
      toast.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      return;
    }
    toast.info('เลื่อนไปตรวจใหม่พรุ่งนี้');
    closeCaseModal();
  };

  // Close after the case is already resolved on the server. NOT handleCloseModal:
  // that releases the claim, and recording a result or deferring has already
  // dropped it — the row leaves every board via the socket event, as with the
  // other case types.
  const closeCaseModal = () => {
    setActiveModal(null);
    setSelectedCase(null);
  };

  const handlePurchaseComplete = async (data) => {
    try {
      const res = await completePurchaseCase(data);
      if (res?.success) {
        toast.success('ดำเนินการเสร็จสิ้น');
        setActiveModal(null); // Close modal
        setSelectedCase(null);
        // Surface the custom-link confirm outcome: a payment link to send (net +Δ),
        // the net refund, and/or the per-item reject refunds created.
        const net = res.data?.netSettlement;
        const refunds = res.data?.refunds || [];
        if (res.paymentUrl || (net && net.direction && net.direction !== 'none') || refunds.length) {
          setConfirmResult({ paymentUrl: res.paymentUrl || null, netSettlement: net || null, refunds });
        }
      } else {
        // Surface the backend's reason (e.g. "not taken by you") instead of a generic
        // toast, so staff don't retry blindly.
        toast.error(res?.error || res?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      }
    } catch (error) {
      console.error('Error completing case:', error);
      toast.error(error?.response?.data?.error || error?.response?.data?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
    }
  };

  // Plan E (owner 2026-11-01): the reviewed request goes to the dispatch bot. The backend clears
  // the claim and hides the case while a job is live; drop the row here too (the 2-minute
  // refresh would do it anyway).
  const handleSendPurchaseToBot = async (purchaseId, pinnedBuyerId = null) => {
    try {
      await DispatchService.sendPurchaseToBot(purchaseId, pinnedBuyerId);
      toast.success('ส่งให้คนกดแล้ว — เคสนี้อยู่กับบอท ดูได้ใน "ออเดอร์ที่ส่งให้บอทกด"');
      setActiveModal(null);
      setSelectedCase(null);
      setDashboardItems((prev) => prev.filter((r) => !(r.type === 'purchase' && (r.source || 'mercari') === 'mercari' && r.id === purchaseId)));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'ส่งให้คนกดไม่สำเร็จ');
    }
  };

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const visibleError = error && error !== dismissedError ? error : null;

  return (
    <Box p={3}>
      {visibleError && (
        <Alert severity="error" onClose={() => setDismissedError(error)} sx={{ mb: 2 }}>
          {visibleError}
        </Alert>
      )}
      {loadError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
          Mercari Admin Dashboard
        </Typography>
        <Box display="flex" gap={1}>
          <Button variant="outlined" onClick={() => openCollections()}>
            รอเก็บเงินส่วนต่าง
          </Button>
          <Button variant="contained" onClick={() => setCreateLinkOpen(true)}>
            สร้างลิงก์สั่งซื้อ
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="80px">No.</TableCell>
              <TableCell width="180px">Admin</TableCell>
              <TableCell width="180px">Date Time</TableCell>
              <TableCell width="150px">Type</TableCell>
              <TableCell width="120px">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!dashboardItems || dashboardItems.length === 0) && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  ไม่มีรายการรอดำเนินการ
                </TableCell>
              </TableRow>
            )}
            {dashboardItems && dashboardItems.length > 0 && dashboardItems.map((item, index) => (
              <TableRow key={`${item.type}-${item.source || 'mercari'}-${item.id}`}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  {item.nickname || ''}
                </TableCell>
                <TableCell>
                  {format(new Date(item.requestedAt), 'MMM d, yyyy HH:mm')}
                </TableCell>
                <TableCell>
                  {getTypeChip(item)}
                </TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleAction(item)}
                    disabled={isLoadingDashboard}
                    sx={{
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      minWidth: '100px'
                    }}
                  >
                    {isLoadingDashboard ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      getActionButtonText(item.type)
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dispatch bot tasks (P4 first slice): shown only when there is something to do */}
      <DispatchAlertsSection />
      {/* Every order the bot took, read-only (pilot tracking) */}
      <Box display="flex" gap={1} alignItems="flex-start" flexWrap="wrap">
        <DispatchJobsSection />
        <DispatchNowSection />
      </Box>

      <ForceTakeModal
        isOpen={activeModal === 'force'}
        onClose={handleCloseModal}
        currentAdmin={currentAdminInfo}
        onForceTake={handleForceTake}
        isLoading={isLoading}
        style={customStyles}
      />

      <PriceNegotiationModal
        isOpen={activeModal === 'price_negotiation'}
        onClose={handleCloseModal}
        item={selectedCase}
        onComplete={handlePriceNegotiationComplete}
        style={customStyles}
      />

      <PurchaseRequestModal
        isOpen={activeModal === 'purchase'}
        onClose={handleCloseModal}
        item={selectedCase}
        onComplete={handlePurchaseComplete}
        onSendToBot={handleSendPurchaseToBot}
        style={customStyles}
      />

      <CustomLinkTaskModal
        isOpen={activeModal === 'customlink'}
        onClose={handleCloseModal}
        item={selectedCase}
        onComplete={handlePurchaseComplete}
        style={customStyles}
      />

      <AppraisalModal
        isOpen={activeModal === 'appraisal'}
        onClose={handleCloseModal}
        item={selectedCase}
        onRecordResult={handleAppraisalResult}
        onPreviewFailure={handleAppraisalFailurePreview}
        onDefer={handleAppraisalDefer}
        style={customStyles}
      />

      <CreateCustomLinkModal
        isOpen={createLinkOpen}
        onClose={() => setCreateLinkOpen(false)}
        shop={shopName}
      />

      {/* Custom-link confirm outcome: net payment link / net refund / reject refunds */}
      <Dialog open={!!confirmResult} onClose={() => setConfirmResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>ผลการยืนยันคำสั่งซื้อ</DialogTitle>
        <DialogContent dividers>
          {confirmResult?.paymentUrl && (
            <Box mb={2}>
              <Typography variant="body2" gutterBottom>
                ลูกค้าต้องจ่ายเพิ่ม (ส่วนต่างรวม)
                {confirmResult.netSettlement?.direction === 'collect' && confirmResult.netSettlement?.amount != null
                  ? ` ฿${Number(confirmResult.netSettlement.amount).toLocaleString()}` : ''} — ส่งลิงก์นี้ให้ลูกค้าชำระ:
              </Typography>
              <Link href={confirmResult.paymentUrl} target="_blank" rel="noopener" sx={{ wordBreak: 'break-all' }}>
                {confirmResult.paymentUrl}
              </Link>
            </Box>
          )}
          {confirmResult?.netSettlement?.direction === 'refund' && (
            <Typography variant="body2" gutterBottom>
              คืนส่วนต่างราคา ฿{Number(confirmResult.netSettlement.amount).toLocaleString()}
              {confirmResult.netSettlement.refundId
                ? ` — สร้างรายการคืนเงิน #${confirmResult.netSettlement.refundId} (รอกรอกบัญชี)`
                : ' — เข้ากระเป๋าเงินลูกค้าแล้ว'}
            </Typography>
          )}
          {confirmResult?.netSettlement?.direction === 'collect' && !confirmResult?.paymentUrl && confirmResult?.netSettlement?.walletTransactionId && (
            <Typography variant="body2" gutterBottom>
              หักส่วนต่างจากกระเป๋าเงินลูกค้าแล้ว ฿{Number(confirmResult.netSettlement.amount).toLocaleString()}
            </Typography>
          )}
          {confirmResult?.refunds?.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              สร้างรายการคืนเงินจากการปฏิเสธ {confirmResult.refunds.length} รายการ (รอกรอกบัญชีในหน้าคืนเงิน)
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {confirmResult?.paymentUrl && (
            <Button onClick={() => { navigator.clipboard.writeText(confirmResult.paymentUrl); toast.success('คัดลอกลิงก์แล้ว'); }}>
              คัดลอกลิงก์
            </Button>
          )}
          <Button onClick={() => setConfirmResult(null)}>ปิด</Button>
        </DialogActions>
      </Dialog>

      {/* Pending reconcile collections — re-find a guest +Δ payment link after the dialog closed */}
      <Dialog open={collectionsOpen} onClose={() => setCollectionsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            รอเก็บเงินส่วนต่าง
            <Button
              size="small"
              onClick={() => { const next = !collectionsShowAll; setCollectionsShowAll(next); openCollections(next); }}
            >
              {collectionsShowAll ? 'แสดงเฉพาะที่รอชำระ' : 'แสดงทั้งหมด'}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {collectionsLoading ? (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
          ) : collections.length === 0 ? (
            <Typography variant="body2" color="text.secondary">ไม่มีรายการรอเก็บเงินส่วนต่าง</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ออเดอร์</TableCell>
                  <TableCell>ลูกค้า</TableCell>
                  <TableCell align="right">ยอด</TableCell>
                  <TableCell>สถานะ</TableCell>
                  <TableCell>ลิงก์</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {collections.map((row) => (
                  <TableRow key={row.beamPurchaseId}>
                    <TableCell>
                      {row.order?.orderCode || `#${row.order?.id ?? '-'}`}
                      {row.orderCount > 1 && (
                        <Typography component="span" variant="caption" color="text.secondary"> +{row.orderCount - 1} ออเดอร์</Typography>
                      )}
                    </TableCell>
                    <TableCell>{row.customer?.smileName || row.customer?.socialId || '-'}</TableCell>
                    <TableCell align="right">฿{Number(row.amountThb).toLocaleString()}</TableCell>
                    <TableCell>{collectionStatusChip(row.status)}</TableCell>
                    <TableCell>
                      {row.paymentUrl ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Link href={row.paymentUrl} target="_blank" rel="noopener" sx={{ wordBreak: 'break-all', fontSize: '0.75rem' }}>
                            {row.paymentUrl}
                          </Link>
                          <Button size="small" onClick={() => copyLink(row.paymentUrl)}>คัดลอก</Button>
                        </Box>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCollectionsOpen(false)}>ปิด</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MercariAdmin;